import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Trash2, X, Upload } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  categoriesAPI,
  subCategoriesAPI,
  type Category,
  type CategoryCreatePayload,
  type SubCategory,
  type SubCategoryCreatePayload,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";

export default function CategoriesSubCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("categories");

  // Pagination state for Categories
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPageSize, setCategoryPageSize] = useState(10);
  const [categoryTotalPages, setCategoryTotalPages] = useState(0);
  const [categoryTotalCount, setCategoryTotalCount] = useState(0);

  // Pagination state for Sub-Categories
  const [subCategoryPage, setSubCategoryPage] = useState(1);
  const [subCategoryPageSize, setSubCategoryPageSize] = useState(10);
  const [subCategoryTotalPages, setSubCategoryTotalPages] = useState(0);
  const [subCategoryTotalCount, setSubCategoryTotalCount] = useState(0);

  // Category state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState<CategoryCreatePayload>({
    name: "",
    description: "",
    is_active: true,
  });
  const [categorySubmitLoading, setCategorySubmitLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(
    null
  );
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);

  // Sub-category state
  const [subCategoryDialogOpen, setSubCategoryDialogOpen] = useState(false);
  const [subCategoryForm, setSubCategoryForm] =
    useState<SubCategoryCreatePayload>({
      name: "",
      category: "",
      description: "",
      is_active: true,
    });
  const [subCategoryNames, setSubCategoryNames] = useState<string[]>([]);
  const [subCategoryInput, setSubCategoryInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subCategorySubmitLoading, setSubCategorySubmitLoading] =
    useState(false);
  const [editingSubCategory, setEditingSubCategory] =
    useState<SubCategory | null>(null);
  const [deletingSubCategory, setDeletingSubCategory] =
    useState<SubCategory | null>(null);
  const [deleteSubCategoryLoading, setDeleteSubCategoryLoading] =
    useState(false);

  const fetchCategories = useCallback(
    async (page = categoryPage, pageSize = categoryPageSize) => {
      setCategoriesLoading(true);
      try {
        const res = await categoriesAPI.list({ page, page_size: pageSize });
        if (Array.isArray(res)) {
          setCategories(res);
          setCategoryTotalCount(res.length);
          setCategoryTotalPages(1);
        } else {
          const list = res.data ?? res.results ?? [];
          const count = res.count ?? res.total_count ?? list.length;
          setCategories(list);
          setCategoryTotalCount(count);
          setCategoryTotalPages(
            res.total_pages ?? Math.ceil(count / pageSize) ?? 1
          );
        }
      } catch {
        toast.error("Failed to load categories");
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    },
    [categoryPage, categoryPageSize]
  );

  const fetchAllCategories = useCallback(async () => {
    try {
      const res = await categoriesAPI.list({ page: 1, page_size: 1000 });
      if (Array.isArray(res)) {
        setAllCategories(res);
      } else {
        setAllCategories(res.data ?? res.results ?? []);
      }
    } catch {
      console.error("Failed to load all categories for dropdown");
    }
  }, []);

  const fetchSubCategories = useCallback(
    async (page = subCategoryPage, pageSize = subCategoryPageSize) => {
      setSubCategoriesLoading(true);
      try {
        const res = await subCategoriesAPI.list({ page, page_size: pageSize });
        if (Array.isArray(res)) {
          setSubCategories(res);
          setSubCategoryTotalCount(res.length);
          setSubCategoryTotalPages(1);
        } else {
          const list = res.data ?? res.results ?? [];
          const count = res.count ?? res.total_count ?? list.length;
          setSubCategories(list);
          setSubCategoryTotalCount(count);
          setSubCategoryTotalPages(
            res.total_pages ?? Math.ceil(count / pageSize) ?? 1
          );
        }
      } catch {
        toast.error("Failed to load sub-categories");
        setSubCategories([]);
      } finally {
        setSubCategoriesLoading(false);
      }
    },
    [subCategoryPage, subCategoryPageSize]
  );

  useEffect(() => {
    if (activeTab === "categories") {
      fetchCategories(categoryPage, categoryPageSize);
    }
  }, [categoryPage, categoryPageSize, activeTab, fetchCategories]);

  useEffect(() => {
    if (activeTab === "subcategories") {
      fetchSubCategories(subCategoryPage, subCategoryPageSize);
      // We also need allCategories for names lookup in the table
      if (allCategories.length === 0) {
        fetchAllCategories();
      }
    }
  }, [
    subCategoryPage,
    subCategoryPageSize,
    activeTab,
    allCategories.length,
    fetchSubCategories,
    fetchAllCategories,
  ]);

  const resetCategoryForm = () => {
    setCategoryForm({ name: "", description: "", is_active: true });
    setEditingCategory(null);
  };

  const resetSubCategoryForm = () => {
    setSubCategoryForm({
      name: "",
      category: "",
      description: "",
      is_active: true,
    });
    setSubCategoryNames([]);
    setSubCategoryInput("");
    setEditingSubCategory(null);
  };

  const handleAddSubCategoryName = (e?: React.KeyboardEvent) => {
    if (e && e.key !== "Enter") {
      return;
    }
    if (e) {
      e.preventDefault();
    }

    const trimmed = subCategoryInput.trim();
    if (trimmed) {
      const names = trimmed
        .split(/[,;\n]+/)
        .map(n => n.trim())
        .filter(n => n !== "");
      setSubCategoryNames(prev => {
        const newNames = [...prev];
        names.forEach(n => {
          if (!newNames.includes(n)) {
            newNames.push(n);
          }
        });
        return newNames;
      });
      setSubCategoryInput("");
    }
  };

  const handleRemoveSubCategoryName = (name: string) => {
    setSubCategoryNames(prev => prev.filter(n => n !== name));
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Please upload a valid CSV file");
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const content = event.target?.result as string;
      if (!content) {
        return;
      }

      const lines = content.split(/\r?\n/);
      const names: string[] = [];

      lines.forEach(line => {
        // Split by comma and take non-empty parts
        const parts = line.split(",").map(p => p.trim());
        parts.forEach(p => {
          if (p && !names.includes(p)) {
            names.push(p);
          }
        });
      });

      if (names.length > 0) {
        setSubCategoryNames(prev => {
          const newNames = [...prev];
          names.forEach(n => {
            if (!newNames.includes(n)) {
              newNames.push(n);
            }
          });
          return newNames;
        });
        toast.success(`Imported ${names.length} names from CSV`);
      } else {
        toast.error("No valid names found in CSV");
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read CSV file");
    };

    reader.readAsText(file);
  };

  const handleOpenCreateCategory = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategory = (cat: Category) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      description: cat.description ?? "",
      is_active: cat.is_active ?? true,
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setCategorySubmitLoading(true);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, {
          name: categoryForm.name.trim(),
          description: categoryForm.description?.trim() ?? undefined,
          is_active: categoryForm.is_active,
        });
        toast.success("Category updated successfully");
      } else {
        await categoriesAPI.create({
          name: categoryForm.name.trim(),
          description: categoryForm.description?.trim() ?? undefined,
          is_active: categoryForm.is_active,
        });
        toast.success("Category created successfully");
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
      fetchAllCategories();
      fetchSubCategories();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message ??
        "Failed to save category";
      toast.error(msg);
    } finally {
      setCategorySubmitLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) {
      return;
    }
    setDeleteCategoryLoading(true);
    try {
      await categoriesAPI.delete(deletingCategory.id);
      toast.success("Category deleted successfully");
      setDeletingCategory(null);
      fetchCategories();
      fetchAllCategories();
      fetchSubCategories();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message ??
        "Failed to delete category";
      toast.error(msg);
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  const handleOpenCreateSubCategory = async () => {
    resetSubCategoryForm();
    await fetchAllCategories();
    setSubCategoryForm(prev => ({
      ...prev,
      category:
        allCategories[0]?.id?.toString() ?? categories[0]?.id?.toString() ?? "",
    }));
    setSubCategoryDialogOpen(true);
  };

  const handleOpenEditSubCategory = useCallback(
    async (sub: SubCategory) => {
      setEditingSubCategory(sub);
      await fetchAllCategories();
      const categoryId =
        sub.category &&
        typeof sub.category === "object" &&
        (sub.category as any).id
          ? String((sub.category as any).id)
          : String(sub.category ?? "");
      setSubCategoryForm({
        name: sub.name,
        category:
          categoryId ||
          (allCategories[0]?.id?.toString() ??
            categories[0]?.id?.toString() ??
            ""),
        description: sub.description ?? "",
        is_active: sub.is_active ?? true,
      });
      setSubCategoryNames([sub.name]);
      setSubCategoryDialogOpen(true);
    },
    [fetchAllCategories, allCategories, categories]
  );

  const handleSaveSubCategory = async () => {
    const finalNames = [...subCategoryNames];
    if (subCategoryInput.trim()) {
      const inputNames = subCategoryInput
        .split(/[,;\n]+/)
        .map(n => n.trim())
        .filter(n => n !== "");
      inputNames.forEach(n => {
        if (!finalNames.includes(n)) {
          finalNames.push(n);
        }
      });
    }

    if (finalNames.length === 0) {
      toast.error("At least one sub-category name is required");
      return;
    }
    if (!subCategoryForm.category) {
      toast.error("Please select a category");
      return;
    }
    setSubCategorySubmitLoading(true);
    try {
      if (editingSubCategory) {
        const payload = {
          name: finalNames[0],
          category:
            Number(subCategoryForm.category) || subCategoryForm.category,
          description: subCategoryForm.description?.trim() ?? undefined,
          is_active: subCategoryForm.is_active,
        };
        await subCategoriesAPI.update(editingSubCategory.id, payload);
        toast.success("Sub-category updated successfully");
      } else {
        // Create sub-categories with name as an array
        await subCategoriesAPI.create({
          name: finalNames,
          category:
            Number(subCategoryForm.category) || subCategoryForm.category,
          description: subCategoryForm.description?.trim() ?? undefined,
          is_active: subCategoryForm.is_active,
        });

        toast.success(
          finalNames.length === 1
            ? "Sub-category created successfully"
            : `${finalNames.length} sub-categories created successfully`
        );
      }
      setSubCategoryDialogOpen(false);
      resetSubCategoryForm();
      fetchSubCategories();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message ??
        "Failed to save sub-category";
      toast.error(msg);
    } finally {
      setSubCategorySubmitLoading(false);
    }
  };

  const handleDeleteSubCategory = async () => {
    if (!deletingSubCategory) {
      return;
    }
    setDeleteSubCategoryLoading(true);
    try {
      await subCategoriesAPI.delete(deletingSubCategory.id);
      toast.success("Sub-category deleted successfully");
      setDeletingSubCategory(null);
      fetchSubCategories();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.detail ??
        error?.message ??
        "Failed to delete sub-category";
      toast.error(msg);
    } finally {
      setDeleteSubCategoryLoading(false);
    }
  };

  const getCategoryName = useCallback(
    (sub: SubCategory) => {
      if (sub.category_name) {
        return sub.category_name;
      }
      const categoryObj = sub.category;
      const id =
        categoryObj && typeof categoryObj === "object" && "id" in categoryObj
          ? (categoryObj as any).id
          : categoryObj;

      if (!id) {
        return "—";
      }

      const cat =
        allCategories.find(c => String(c.id) === String(id)) ??
        categories.find(c => String(c.id) === String(id));
      return cat?.name ?? String(id);
    },
    [allCategories, categories]
  );

  const categoryTableColumns = useMemo<TableColumnDef<Category>[]>(
    () => [
      {
        id: "sr",
        label: "#",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (categoryPage - 1) * categoryPageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: cat => <span className="font-medium">{cat.name}</span>,
      },
      {
        id: "description",
        label: "Description",
        width: 200,
        renderCell: cat => (
          <span className="max-w-[200px] truncate text-muted-foreground">
            {cat.description ?? "—"}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: cat => (
          <span
            className={
              cat.is_active !== false
                ? "text-green-600"
                : "text-muted-foreground"
            }
          >
            {cat.is_active !== false ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 120,
        alwaysVisible: true,
        renderCell: cat => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEditCategory(cat)}
              aria-label="Edit category"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingCategory(cat)}
              aria-label="Delete category"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [categoryPage, categoryPageSize]
  );

  const subCategoryTableColumns = useMemo<TableColumnDef<SubCategory>[]>(
    () => [
      {
        id: "sr",
        label: "#",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (subCategoryPage - 1) * subCategoryPageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: sub => <span className="font-medium">{sub.name}</span>,
      },
      {
        id: "category",
        label: "Category",
        width: 140,
        renderCell: sub => getCategoryName(sub),
      },
      {
        id: "description",
        label: "Description",
        width: 200,
        renderCell: sub => (
          <span className="max-w-[200px] truncate text-muted-foreground">
            {sub.description ?? "—"}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: sub => (
          <span
            className={
              sub.is_active !== false
                ? "text-green-600"
                : "text-muted-foreground"
            }
          >
            {sub.is_active !== false ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 120,
        alwaysVisible: true,
        renderCell: sub => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEditSubCategory(sub)}
              aria-label="Edit sub-category"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingSubCategory(sub)}
              aria-label="Delete sub-category"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [
      subCategoryPage,
      subCategoryPageSize,
      getCategoryName,
      handleOpenEditSubCategory,
    ]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Categories & Sub-Categories
        </h1>
        <p className="text-muted-foreground">
          Manage categories and sub-categories used across the platform
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="subcategories">Sub-Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <DataTableProvider
            tableId="super-admin-categories"
            columns={categoryTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Categories ({categoryTotalCount})</CardTitle>
                <div className="flex items-center gap-2">
                  <Dialog
                    open={categoryDialogOpen}
                    onOpenChange={open => {
                      setCategoryDialogOpen(open);
                      if (!open) {
                        resetCategoryForm();
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        onClick={handleOpenCreateCategory}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Create new category
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingCategory
                            ? "Edit Category"
                            : "Create New Category"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="cat-name">Name *</Label>
                          <Input
                            id="cat-name"
                            placeholder="Category name"
                            value={categoryForm.name}
                            onChange={e =>
                              setCategoryForm(prev => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor="cat-desc">Description</Label>
                          <Textarea
                            id="cat-desc"
                            placeholder="Optional description"
                            value={categoryForm.description ?? ""}
                            onChange={e =>
                              setCategoryForm(prev => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            rows={3}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="cat-active"
                            checked={categoryForm.is_active ?? true}
                            onCheckedChange={checked =>
                              setCategoryForm(prev => ({
                                ...prev,
                                is_active: checked,
                              }))
                            }
                          />
                          <Label htmlFor="cat-active">Active</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setCategoryDialogOpen(false)}
                            disabled={categorySubmitLoading}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveCategory}
                            disabled={categorySubmitLoading}
                          >
                            {categorySubmitLoading ? (
                              <span className="flex items-center gap-2">
                                <CustomLoader size="xs" />
                                Saving...
                              </span>
                            ) : editingCategory ? (
                              "Update"
                            ) : (
                              "Create"
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <DataTableColumnSettings columns={categoryTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {categoriesLoading ? (
                  <div className="flex justify-center py-12 text-muted-foreground">
                    <CustomLoader size="md" />
                  </div>
                ) : categories.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    No categories yet. Create one to get started.
                  </p>
                ) : (
                  <>
                    <DataTable
                      embedded
                      hideToolbar
                      tableId="super-admin-categories"
                      columns={categoryTableColumns}
                      data={categories}
                      getRowKey={cat => cat.id}
                    />
                    <CommonPagination
                      currentPage={categoryPage}
                      totalPages={categoryTotalPages}
                      pageSize={categoryPageSize}
                      totalCount={categoryTotalCount}
                      onPageChange={setCategoryPage}
                      onPageSizeChange={size => {
                        setCategoryPageSize(size);
                        setCategoryPage(1);
                      }}
                      className="mt-6 border-t pt-4"
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </DataTableProvider>
        </TabsContent>

        <TabsContent value="subcategories" className="space-y-4">
          <DataTableProvider
            tableId="super-admin-sub-categories"
            columns={subCategoryTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Sub-Categories ({subCategoryTotalCount})</CardTitle>
                <div className="flex items-center gap-2">
                  <Dialog
                    open={subCategoryDialogOpen}
                    onOpenChange={open => {
                      setSubCategoryDialogOpen(open);
                      if (!open) {
                        resetSubCategoryForm();
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        onClick={handleOpenCreateSubCategory}
                        className="gap-2"
                        disabled={
                          allCategories.length === 0 && categories.length === 0
                        }
                      >
                        <Plus className="h-4 w-4" />
                        Create new sub-category
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {editingSubCategory
                            ? "Edit Sub-Category"
                            : "Create New Sub-Category"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="subcat-category">Category *</Label>
                          <Select
                            value={String(subCategoryForm.category)}
                            onValueChange={v =>
                              setSubCategoryForm(prev => ({
                                ...prev,
                                category: v,
                              }))
                            }
                          >
                            <SelectTrigger id="subcat-category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent
                              side="bottom"
                              className="max-h-[200px]"
                            >
                              {allCategories.length > 0
                                ? allCategories.map(c => (
                                    <SelectItem key={c.id} value={String(c.id)}>
                                      {c.name}
                                    </SelectItem>
                                  ))
                                : categories.map(c => (
                                    <SelectItem key={c.id} value={String(c.id)}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <div className="mb-4 flex items-center justify-between">
                            <div className="flex flex-col items-start gap-2">
                              <Label htmlFor="subcat-name">
                                {editingSubCategory ? "Name *" : "Names*"}
                              </Label>
                              <Label htmlFor="subcat-name">
                                {editingSubCategory
                                  ? ""
                                  : " (press Enter or use commas to add multiple)"}
                              </Label>
                            </div>

                            {!editingSubCategory && (
                              <>
                                <input
                                  type="file"
                                  accept=".csv"
                                  className="hidden"
                                  ref={fileInputRef}
                                  onChange={handleCsvUpload}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1.5 border border-dashed border-blue-700 text-xs"
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  <Upload className="h-3.5 w-3.5" />
                                  Import CSV
                                </Button>
                              </>
                            )}
                          </div>
                          <div className="mb-2 mt-1.5 flex max-h-32 flex-wrap gap-2 overflow-y-auto rounded-md border border-dashed p-1 empty:hidden">
                            {subCategoryNames.map(name => (
                              <Badge
                                key={name}
                                variant="secondary"
                                className="flex items-center gap-1 px-2.5 py-1"
                              >
                                {name}
                                {!editingSubCategory && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveSubCategoryName(name)
                                    }
                                    className="ml-0.5 rounded-full hover:bg-muted focus:outline-none"
                                  >
                                    <X className="h-3 w-3" />
                                    <span className="sr-only">
                                      Remove {name}
                                    </span>
                                  </button>
                                )}
                              </Badge>
                            ))}
                          </div>
                          <Input
                            id="subcat-name"
                            placeholder={
                              editingSubCategory
                                ? "Sub-category name"
                                : "Type name and press Enter..."
                            }
                            value={
                              editingSubCategory
                                ? subCategoryNames[0] || ""
                                : subCategoryInput
                            }
                            onChange={e => {
                              if (editingSubCategory) {
                                setSubCategoryNames([e.target.value]);
                              } else {
                                setSubCategoryInput(e.target.value);
                              }
                            }}
                            onKeyDown={
                              editingSubCategory
                                ? undefined
                                : handleAddSubCategoryName
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor="subcat-desc">Description</Label>
                          <Textarea
                            id="subcat-desc"
                            placeholder="Optional description"
                            value={subCategoryForm.description ?? ""}
                            onChange={e =>
                              setSubCategoryForm(prev => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            rows={3}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="subcat-active"
                            checked={subCategoryForm.is_active ?? true}
                            onCheckedChange={checked =>
                              setSubCategoryForm(prev => ({
                                ...prev,
                                is_active: checked,
                              }))
                            }
                          />
                          <Label htmlFor="subcat-active">Active</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setSubCategoryDialogOpen(false)}
                            disabled={subCategorySubmitLoading}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveSubCategory}
                            disabled={subCategorySubmitLoading}
                          >
                            {subCategorySubmitLoading ? (
                              <span className="flex items-center gap-2">
                                <CustomLoader size="xs" />
                                Saving...
                              </span>
                            ) : editingSubCategory ? (
                              "Update"
                            ) : (
                              "Create"
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <DataTableColumnSettings columns={subCategoryTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {subCategoriesLoading ? (
                  <div className="flex justify-center py-12 text-muted-foreground">
                    <CustomLoader size="md" />
                  </div>
                ) : categories.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    Add at least one category before creating sub-categories.
                  </p>
                ) : subCategories.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    No sub-categories yet. Create one to get started.
                  </p>
                ) : (
                  <>
                    <DataTable
                      embedded
                      hideToolbar
                      tableId="super-admin-sub-categories"
                      columns={subCategoryTableColumns}
                      data={subCategories}
                      getRowKey={sub => sub.id}
                    />
                    <CommonPagination
                      currentPage={subCategoryPage}
                      totalPages={subCategoryTotalPages}
                      pageSize={subCategoryPageSize}
                      totalCount={subCategoryTotalCount}
                      onPageChange={setSubCategoryPage}
                      onPageSizeChange={size => {
                        setSubCategoryPageSize(size);
                        setSubCategoryPage(1);
                      }}
                      className="mt-6 border-t pt-4"
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </DataTableProvider>
        </TabsContent>
      </Tabs>

      {/* Delete Category Dialog */}
      <Dialog
        open={!!deletingCategory}
        onOpenChange={open => !open && setDeletingCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          {deletingCategory && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{deletingCategory.name}</strong>? This may affect
                sub-categories linked to it.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeletingCategory(null)}
                  disabled={deleteCategoryLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteCategory}
                  disabled={deleteCategoryLoading}
                >
                  {deleteCategoryLoading ? (
                    <CustomLoader size="xs" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Sub-Category Dialog */}
      <Dialog
        open={!!deletingSubCategory}
        onOpenChange={open => !open && setDeletingSubCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Sub-Category</DialogTitle>
          </DialogHeader>
          {deletingSubCategory && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{deletingSubCategory.name}</strong>?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeletingSubCategory(null)}
                  disabled={deleteSubCategoryLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteSubCategory}
                  disabled={deleteSubCategoryLoading}
                >
                  {deleteSubCategoryLoading ? (
                    <CustomLoader size="xs" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
